'use memo'

import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import type {
  RosterPlayerListItem,
  RosterSearchPlayer,
} from '@sportninja/common/features/team/player'
import { useModalScroll } from 'src/hooks/useModalScroll'
import { RosterPlayerProvider } from './RosterPlayerContext'
import RosterPlayerModalContent from './RosterPlayerModalContent'
import type { ModalTrailItem } from 'src/features/profile/player/setupModal/types/modalTrail'

const TRANSITION_DURATION = 300

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  teamId: string
  rosterId: string
  sportId: string | null
  sportName?: string
  hasAdminPermission?: boolean
  /** Roster's require_email flag → makes the player email mandatory. */
  requireEmail?: boolean
  /**
   * Show the Family Members section when editing (team roster, Operations,
   * Team Budget). Off by default for hosts that don't manage family.
   */
  familyMembers?: boolean
  /**
   * Add-existing mode: the copy target id. Personal fields come from
   * `searchPlayer` (no fetch); Save uses the copy endpoint. Absent (and no
   * editPlayer) → create-new mode.
   */
  playerId?: string
  /** Add-existing: the picked search row (name/email/image) that seeds the form. */
  searchPlayer?: RosterSearchPlayer
  /**
   * Edit mode: the existing roster player row. Personal + roster fields are
   * prefilled from the row (no fetch), Save uses the PUT update endpoint. Takes
   * precedence over `playerId`.
   */
  editPlayer?: RosterPlayerListItem
  /**
   * SN-8449 — the z-index of the overlay this editor is opened FROM, when it is
   * opened from one (the Team Budget sheet inside the admin Registration Details
   * modal). The editor then stacks above it and covers the whole viewport. Absent
   * on a page.
   */
  hostZIndex?: number
  /** Where the editor was opened from, for its breadcrumb ("Team › Roster"). */
  trail?: ModalTrailItem[]
}

const RosterPlayerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  rosterId,
  sportId,
  sportName,
  hasAdminPermission = false,
  requireEmail = false,
  familyMembers = false,
  playerId,
  searchPlayer,
  editPlayer,
  hostZIndex,
  trail,
}) => {
  const { openModal, closeModal } = useModalScroll()
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      openModal()
      setIsMounted(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true))
      })
    } else {
      setIsVisible(false)
      const timer = setTimeout(() => {
        setIsMounted(false)
        closeModal()
      }, TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isOpen, openModal, closeModal])

  if (!isMounted) {
    return null
  }

  // Portal to #modal-root (same target as Sheet) so the full-screen overlay
  // escapes whatever container it's rendered from — e.g. the Team page mounts
  // this feature's gate inside a hidden, zero-size `visibility:hidden` span,
  // which would otherwise hide/clip an inline-rendered overlay.
  const portalTarget = document.getElementById('modal-root') ?? document.body

  return ReactDOM.createPortal(
    <RosterPlayerProvider
      teamId={teamId}
      rosterId={rosterId}
      sportId={sportId}
      sportName={sportName}
      hasAdminPermission={hasAdminPermission}
      requireEmail={requireEmail}
      familyMembers={familyMembers}
      playerId={playerId}
      searchPlayer={searchPlayer}
      editPlayer={editPlayer}
      onClose={onClose}
      onSuccess={onSuccess}
    >
      <RosterPlayerModalContent
        isVisible={isVisible}
        transitionDuration={TRANSITION_DURATION}
        hostZIndex={hostZIndex}
        trail={trail}
      />
    </RosterPlayerProvider>,
    portalTarget
  )
}

export default RosterPlayerModal
